import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { authHeader } from "@/utils/headers";
import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { ChatMessageI } from "@/components/Playground/types";

export interface GetPlaygroundSessionChatMessagesResponseI {
  success: boolean;
  messages: ChatMessageI[];

  total: number;
  total_messages: number;
  page: number;
  per_page: number;
  has_next: boolean;
  has_prev: boolean;

  next_num: number | null;
  pages: number;
  prev_num: number | null;

  total_cost: number;
  total_input_tokens: number;
  total_latency_ms: number;
  total_output_tokens: number;
}

export interface GetPlaygroundSessionChatMessagesParamsI {
  workspaceId: number;
  sessionId: string;
  variableSetIndex: number;
  page?: number;
  perPage?: number;
}

export const useAPIGetPlaygroundSessionChatMessages = (
  params: GetPlaygroundSessionChatMessagesParamsI,
  options?: Omit<
    UseQueryOptions<GetPlaygroundSessionChatMessagesResponseI>,
    "queryKey" | "queryFn"
  >,
) => {
  const userToken = useAuth()?.userToken;

  const { workspaceId, sessionId, variableSetIndex, page = 1, perPage = 10 } = params;

  return useQuery<GetPlaygroundSessionChatMessagesResponseI>({
    queryKey: [
      "playground-session-chat-messages",
      workspaceId,
      sessionId,
      variableSetIndex,
      page,
      perPage,
    ],
    queryFn: async () => {
      const baseUrl = ENDPOINTS.playground_session_chat_messages(workspaceId, sessionId);

      const url = new URL(baseUrl);
      url.searchParams.set("variable_set_index", String(variableSetIndex));
      url.searchParams.set("page", String(page));
      url.searchParams.set("per_page", String(perPage));

      const response = await fetch(url.toString(), {
        headers: authHeader(userToken!),
      });
      return handleResponse(response);
    },
    enabled: !!userToken && !!sessionId,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    ...options,
  });
};
